// M12 — Müşteri branding + kurumsal DOCX (PLAN-M12-CLIENT-BRANDING §5).
// Per-client override + system default fallback + hardcoded fallback.

import { z } from "zod";

// Word-safe fonts (PLAN §2 Karar B — hibrit genişletme).
// Office 2016+ Windows + macOS Word'de garantili yüklü modern set.
// UI'da bu liste dropdown + "Diğer (Özel)" custom input seçeneği sunar.
// Custom font Word'de yüklü değilse otomatik substitute olur (uyarı UI'da).
export const HEADING_FONTS = [
  "Cambria",
  "Georgia",
  "Times New Roman",
  "Garamond",
  "Book Antiqua",
  "Palatino Linotype",
  "Constantia",
  "Bookman Old Style",
] as const;

export const BODY_FONTS = [
  "Calibri",
  "Arial",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
  "Segoe UI",
  "Corbel",
  "Candara",
  "Century Gothic",
  "Franklin Gothic Book",
  "Lucida Sans Unicode",
] as const;

// Brand storage'da font string'i: curated list'ten biri veya custom (Inter, Roboto vs.).
// Schema z.string() — pixel-perfect garanti curated için, custom Word substitute riski var.

// Hex color #RRGGBB (with or without leading #)
const HexColorSchema = z.string().regex(/^#?[0-9A-Fa-f]{6}$/, "Hex renk #RRGGBB formatında olmalı");

// Header/Footer text — token destekli (PLAN §2 Karar E; {period} raporlama dönemi
// spec 2026-09-22 K8 ile eklendi):
//   {client_name} {framework_name} {year} {period} {date} {page} {total_pages}
// {page} ve {total_pages} docx native field, runtime interpolation değil.
const TemplateTextSchema = z.string().max(200);

export const BrandingConfigSchema = z.object({
  enabled: z.boolean().default(true),
  colors: z.object({
    primary: HexColorSchema, // Accent (cover bar, H1 underline)
    secondary: HexColorSchema, // Table header bg, ikincil vurgu
    text: HexColorSchema, // Body text default
    bgAccent: HexColorSchema, // Cover/section block bg (warm)
    muted: HexColorSchema, // Footer text, alt başlıklar
  }),
  fonts: z.object({
    // Custom font için string (curated list dışı brand fontu olabilir — Inter, Roboto vs.).
    // UI curated dropdown + "Diğer" custom input ile besler; pixel-perfect garantisi sadece
    // curated için, custom Word substitute'a düşer ama brand-spesifik fontu mümkün kılar.
    heading: z.string().min(1).max(60),
    body: z.string().min(1).max(60),
  }),
  templates: z.object({
    headerText: TemplateTextSchema,
    footerLeftText: TemplateTextSchema,
    footerRightText: TemplateTextSchema,
  }),
  // DOCX bölüm başlığında bölüm kodunu göster ("SF-RAKAMLAR — Rakamlarla X").
  // Finar 2026-10-20: müşteri raporunda kod istenmiyor → varsayılan false (yalnız etiket).
  // Eski kayıtlarda alan yok → parse'ta false; snapshot'ta donar.
  showSectionCodes: z.boolean().default(false),
  // Bölüm sonunda küçük puntolu "İlgili standart: <çerçeve> <kod> — <etiket>" dipnotu (Finar K7:
  // standart kodu anlatıda değil dipnotta). Eski snapshot'larda alan yok → parse'ta true.
  showStandardRefs: z.boolean().default(true),
});
export type BrandingConfig = z.infer<typeof BrandingConfigSchema>;

// Hardcoded fallback (en alt katman). Tasarım sistemi §0.1 ile uyumlu.
export const HARDCODED_DEFAULT_BRANDING: BrandingConfig = {
  enabled: true,
  colors: {
    primary: "#C44536", // terra-cotta
    secondary: "#8B7E74", // warm beige darker
    text: "#1A1A1A",
    bgAccent: "#F5EFE6", // warm beige
    muted: "#8B7E74",
  },
  fonts: {
    heading: "Cambria",
    body: "Calibri",
  },
  templates: {
    headerText: "{client_name} · {framework_name} · {year}",
    footerLeftText: "{date}",
    footerRightText: "Sayfa {page} / {total_pages}",
  },
  showSectionCodes: false,
  showStandardRefs: true,
};

// Effective branding — DOCX export'a giden resolved shape.
// Resolution: client (enabled=true) → system_default → hardcoded.
export const EffectiveBrandingSchema = BrandingConfigSchema.extend({
  logoStoragePath: z.string().nullable(),
  source: z.enum(["client", "system_default", "hardcoded"]),
});
export type EffectiveBranding = z.infer<typeof EffectiveBrandingSchema>;

// API request/response shapes
export const UpdateBrandingRequestSchema = BrandingConfigSchema;
export type UpdateBrandingRequest = z.infer<typeof UpdateBrandingRequestSchema>;

export const BrandingLogoPresignRequestSchema = z.object({
  fileName: z.string().min(1).max(120),
  mimeType: z.enum(["image/png", "image/jpeg"]),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(5 * 1024 * 1024), // 5MB
});
export type BrandingLogoPresignRequest = z.infer<typeof BrandingLogoPresignRequestSchema>;

export const BrandingLogoPresignResponseSchema = z.object({
  uploadUrl: z.string().url(),
  storagePath: z.string(),
  expiresIn: z.number().int().positive(),
});
export type BrandingLogoPresignResponse = z.infer<typeof BrandingLogoPresignResponseSchema>;

export const BrandingLogoCommitRequestSchema = z.object({
  storagePath: z.string().min(1),
});
export type BrandingLogoCommitRequest = z.infer<typeof BrandingLogoCommitRequestSchema>;

// GET response: effective branding + logo preview URL (kısa-ömür presigned)
export const BrandingResponseSchema = z.object({
  config: BrandingConfigSchema.nullable(), // null = system default kullanılıyor (client mode)
  effective: EffectiveBrandingSchema,
  logoPreviewUrl: z.string().url().nullable(),
});
export type BrandingResponse = z.infer<typeof BrandingResponseSchema>;

// Audit log action enum extensions (M12)
export const BRANDING_AUDIT_ACTIONS = [
  "client.branding.update",
  "client.branding.logo_update",
  "client.branding.logo_delete",
  "client.branding.disable",
  "settings.branding_default.update",
  "settings.branding_default.logo_update",
  "settings.branding_default.logo_delete",
] as const;
export type BrandingAuditAction = (typeof BRANDING_AUDIT_ACTIONS)[number];
